import { useEffect, useRef, useState, ReactNode, memo } from "react";
import { SxProps, Theme, Tooltip, Typography } from "@mui/material";

/**
 * For this component to work properly its's parent should have a display: "flex" and the desired maxWidth
 * If width is unknown use the ResizeObverser hook to truncate the text according to the available space.
 */
const TruncatedText = memo(function TruncatedText({
    width,
    children,
    tooltipPlacement = "top-start",
    sx,
}: {
    width?: number;
    children: ReactNode;
    tooltipPlacement?: "top-start" | "bottom-start";
    sx?: SxProps<Theme>;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

    useEffect(() => {
        if (ref.current === null) return;
        setShouldShowTooltip(ref.current?.scrollWidth > ref.current?.clientWidth);
    }, [width]);

    const Text = (
        <Typography
            ref={ref}
            sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "pre",
                lineHeight: "inherit",
                flex: 1,
                ...sx,
            }}
        >
            {children}
        </Typography>
    );

    return shouldShowTooltip ? (
        <Tooltip arrow placement={tooltipPlacement} title={children}>
            {Text}
        </Tooltip>
    ) : (
        Text
    );
});

export default TruncatedText;
